import { useState } from 'react';
import Icon from '../../components/common/Icon';
import type { TourAreaMode } from '../../utils/tourAreas';
import styles from './TourAreaRail.module.css';

// The Scene rail's list of Virtual Tour areas (Advanced Virtual Tour): how the
// scans split into areas (a project setting), each area's name — it takes you
// there — and the pencil that renames it for everyone on the project. An empty
// name gives the area its automatic name back.

export const AREA_MODES: Array<{ value: TourAreaMode; label: string; hint: string }> = [
  { value: 'gaps', label: 'Gaps between scans', hint: 'Scans the tour rings connect are one area; a gap no ring crosses starts another' },
  { value: 'file', label: 'Point cloud files', hint: 'One area per file (split where its scans do not connect)' },
  { value: 'floor', label: 'Floors', hint: 'One area per storey, from the floor height under each scan' },
  { value: 'custom', label: 'Areas drawn by hand', hint: 'Draw the areas on a plan of the scans; scans in none are split by gaps' },
];

export interface RailArea {
  key: string;
  name: string;
  colour: string;
  scans: number;
  named: boolean;
  here: boolean;
}

interface Props {
  areas: RailArea[];
  // Renaming, choosing how areas are made and drawing them: owners and editors.
  canRename: boolean;
  mode: TourAreaMode;
  onMode: (mode: TourAreaMode) => Promise<void>;
  onDraw: () => void;
  onGo: (key: string) => void;
  onRename: (key: string, name: string) => Promise<void>;
}

export const AREA_NAME_MAX = 60;

const TourAreaRail = ({ areas, canRename, mode, onMode, onDraw, onGo, onRename }: Props) => {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = (a: RailArea) => {
    setEditing(a.key);
    setDraft(a.named ? a.name : '');
    setError(null);
  };
  const save = async (a: RailArea) => {
    if (saving) return;
    const name = draft.trim();
    if ((a.named ? a.name : '') === name) { setEditing(null); return; }
    setSaving(true);
    try {
      await onRename(a.key, name);
      setEditing(null);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The name could not be saved');
    } finally {
      setSaving(false);
    }
  };

  const changeMode = async (next: TourAreaMode) => {
    setError(null);
    try {
      await onMode(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The setting could not be saved');
    }
  };
  const current = AREA_MODES.find((m) => m.value === mode) ?? AREA_MODES[0];

  return (
    <div className={styles.list}>
      <div className={styles.mode}>
        <label className={styles.modeLabel}>
          <span>Make areas from</span>
          <select
            className={styles.modeSelect}
            value={current.value}
            disabled={!canRename}
            title={canRename ? current.hint : `${current.hint}. Owners and editors choose this.`}
            onChange={(e) => void changeMode(e.target.value as TourAreaMode)}
          >
            {AREA_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </label>
        {mode === 'custom' && canRename && (
          <button type="button" className={styles.draw} onClick={onDraw}>
            <Icon name="polyline" style={{ fontSize: 14 }} />
            Draw areas
          </button>
        )}
      </div>
      {areas.map((a) => (
        <div key={a.key} className={styles.row}>
          <span className={styles.dot} style={{ background: a.colour }} aria-hidden="true" />
          {editing === a.key ? (
            <form
              className={styles.form}
              onSubmit={(e) => { e.preventDefault(); void save(a); }}
            >
              <input
                autoFocus
                className={styles.input}
                value={draft}
                maxLength={AREA_NAME_MAX}
                placeholder={a.named ? 'Empty = automatic name' : a.name}
                aria-label={`Name for ${a.name}`}
                disabled={saving}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Escape') { setEditing(null); setError(null); }
                }}
              />
              <button type="submit" className={styles.iconBtn} disabled={saving} aria-label="Save the name">
                <Icon name="check" style={{ fontSize: 14 }} />
              </button>
              <button type="button" className={styles.iconBtn} disabled={saving} aria-label="Cancel" onClick={() => { setEditing(null); setError(null); }}>
                <Icon name="close" style={{ fontSize: 14 }} />
              </button>
            </form>
          ) : (
            <>
              <button type="button" className={styles.name} onClick={() => onGo(a.key)} title={`Go to ${a.name}`}>
                {a.name}
              </button>
              {a.here && <span className={styles.here}>here</span>}
              <span className={styles.scans}>{a.scans}</span>
              {canRename && (
                <button type="button" className={styles.iconBtn} aria-label={`Rename ${a.name}`} onClick={() => start(a)}>
                  <Icon name="edit" style={{ fontSize: 13 }} />
                </button>
              )}
            </>
          )}
        </div>
      ))}
      {error && <p className={styles.error} role="alert">{error}</p>}
    </div>
  );
};

export default TourAreaRail;
